import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaCheck,
  FaMinus,
  FaPlus,
  FaShoppingBag,
  FaTrash,
} from "react-icons/fa";
import axios from "axios";
import axiosInstance from "../../../api/axiosInstance";
import { getMenuByRestaurant } from "../../../api/menuApi";
import LoadingButton from "../../LoadingButton";
import { CustomerDetails } from "../types";

export type CartItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  instructions?: string;
};

type Props = {
  cartItems: CartItem[];
  restaurantGuid: string;
  tableId: string;
  tableName?: string;
  customerDetails: CustomerDetails;
  onBack: () => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onUpdateInstructions: (itemId: string, instructions: string) => void;
  onRemoveItem: (itemId: string) => void;
  onPlaceOrder: () => void;
};

const CartPage: React.FC<Props> = ({
  cartItems,
  restaurantGuid,
  tableId,
  tableName = "",
  customerDetails,
  onBack,
  onUpdateQuantity,
  onUpdateInstructions,
  onRemoveItem,
  onPlaceOrder,
}) => {
  const [editingInstructions, setEditingInstructions] = useState<string | null>(null);
  const [instructionText, setInstructionText] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [menu, setMenu] = useState<any[]>([]);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const total = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );

  const totalItems = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  useEffect(() => {
    const stored = localStorage.getItem("menu");

    if (stored) {
      setMenu(JSON.parse(stored));
      return;
    }

    getMenuByRestaurant(restaurantGuid)
      .then((menuData) => {
        setMenu(menuData);
        localStorage.setItem("menu", JSON.stringify(menuData));
      })
      .catch((err) => console.error("Error loading menu:", err));
  }, [restaurantGuid]);

  const handlePlaceOrder = async () => {
    if (isPlacingOrder) {
      return;
    }

    setIsPlacingOrder(true);
    setError("");
    setSuccess("");

    if (cartItems.length === 0) {
      setError("Your cart is empty.");
      setIsPlacingOrder(false);
      return;
    }

    try {
      const payload = {
        restaurantGuid,
        placedBy: "",
        tableId: tableId.toString(),
        customerId: customerDetails.id || "",
        orderType: "DINE_IN",
        orderSource: "QUICK_SERVE",
        items: cartItems.map((item) => {
          const menuItem = menu.find((m) => m.id === item.id);

          return {
            menuItemId: item.id,
            quantity: item.quantity,
            price: menuItem?.price || null,
            discountedPrice: menuItem?.discountPrice || null,
            cookingInstructions: item.instructions,
            estimatedTimeInMin: menuItem?.estimatedTimeInMin || null,
          };
        }),
      };

      await axiosInstance.post(`/Order/PlaceOrder`, payload);

      setSuccess("Order placed successfully!");

      setTimeout(() => {
        setSuccess("");
        onPlaceOrder();
      }, 1800);
    } catch (err) {
      console.error(err);

      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || "Failed to place order.");
      } else {
        setError("Failed to place order.");
      }

      setTimeout(() => {
        setError("");
      }, 2500);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const openInstructionEditor = (itemId: string, currentText: string) => {
    setEditingInstructions(itemId);
    setInstructionText(currentText || "");
  };

  const saveInstruction = (itemId: string) => {
    onUpdateInstructions(itemId, instructionText);
    setEditingInstructions(null);
    setInstructionText("");
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden text-slate-900 dark:text-white"
      style={{
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <header className="sticky top-0 z-30 border-b border-slate-200/60 bg-gray-100/90 backdrop-blur-xl dark:border-slate-800 dark:bg-gray-900/90">
        <div className="mx-auto max-w-6xl px-3 py-3 sm:px-4 sm:py-4">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 transition active:scale-90 dark:bg-slate-900 dark:text-white dark:ring-slate-700"
            >
              <FaArrowLeft size={14} />
            </button>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="truncate text-base font-black text-slate-950 dark:text-slate-100 sm:text-xl">
                    Review Order
                  </h1>

                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {tableName && (
                      <span className="max-w-full truncate rounded-full bg-slate-950 px-2.5 py-1 text-[11px] font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
                        {tableName}
                      </span>
                    )}
                    <span className="max-w-full truncate rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                      {customerDetails.customerName || "Guest"}
                    </span>
                    <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                      {totalItems} item{totalItems === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                <div className="rounded-full bg-orange-500 px-3 py-2 text-[12px] font-bold text-white shadow-sm sm:px-4 sm:text-sm">
                  {"\u20B9"}{total.toFixed(0)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-3 py-3 pb-[120px] sm:px-4 sm:py-4">
        {cartItems.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white/70 px-6 py-12 text-center dark:border-slate-700 dark:bg-slate-900/50">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-300">
              <FaShoppingBag size={18} />
            </div>

            <p className="mt-4 text-[14px] font-semibold text-slate-700 dark:text-slate-300">
              Your cart is empty
            </p>

            <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">
              Add items to continue
            </p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="rounded-3xl border border-slate-200/70 bg-white/90 p-3 dark:border-slate-800 dark:bg-slate-900/90"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-1 text-[13px] font-bold text-slate-900 dark:text-white">
                      {item.name}
                    </h3>

                    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                      {"\u20B9"}{item.price.toFixed(0)} each
                    </p>

                    {item.instructions && editingInstructions !== item.id && (
                      <div className="mt-2 rounded-2xl bg-orange-50 px-3 py-2 text-[11px] text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                        {item.instructions}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-500 transition active:scale-90 dark:bg-red-500/10 dark:text-red-300"
                  >
                    <FaTrash size={11} />
                  </button>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2">
                  <button
                    onClick={() => openInstructionEditor(item.id, item.instructions || "")}
                    className="rounded-full bg-slate-100 px-3 py-2 text-[11px] font-semibold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-300"
                  >
                    {item.instructions ? "Edit note" : "Add note"}
                  </button>

                  <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 dark:bg-slate-800">
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity === 1}
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-slate-700 transition active:scale-90 disabled:opacity-40 dark:bg-slate-700 dark:text-white"
                    >
                      <FaMinus size={9} />
                    </button>

                    <span className="min-w-[18px] text-center text-[11px] font-bold">
                      {item.quantity}
                    </span>

                    <button
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white transition active:scale-90"
                    >
                      <FaPlus size={9} />
                    </button>
                  </div>
                </div>

                {editingInstructions === item.id && (
                  <div className="mt-3 space-y-2">
                    <input
                      type="text"
                      value={instructionText}
                      onChange={(e) => setInstructionText(e.target.value)}
                      placeholder="Cooking instructions"
                      className="h-10 w-full rounded-2xl border border-slate-200 bg-white px-3 text-[12px] outline-none focus:border-orange-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => saveInstruction(item.id)}
                        className="rounded-2xl bg-slate-950 py-2 text-[11px] font-bold text-white dark:bg-white dark:text-slate-950"
                      >
                        Save
                      </button>

                      <button
                        onClick={() => setEditingInstructions(null)}
                        className="rounded-2xl border border-slate-200 py-2 text-[11px] font-bold text-slate-700 dark:border-slate-700 dark:text-slate-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {cartItems.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.7rem+env(safe-area-inset-bottom))] sm:px-4 md:px-6 lg:left-64 lg:right-0 lg:px-8">
          <div className="pointer-events-auto mx-auto max-w-6xl rounded-3xl border border-slate-200/70 bg-white/90 p-2 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/90">
            <div className="flex items-center justify-between gap-3 px-1 pb-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">
                  Total
                </p>

                <p className="text-[16px] font-black text-slate-900 dark:text-white">
                  {"\u20B9"}{total.toFixed(2)}
                </p>
              </div>

              <div className="rounded-full bg-orange-50 px-3 py-1.5 text-[11px] font-bold text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                {totalItems} items
              </div>
            </div>

            <LoadingButton
              text="Place Order"
              loadingText="Placing..."
              loading={isPlacingOrder}
              onClick={handlePlaceOrder}
            />
          </div>
        </div>
      )}

      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center dark:bg-slate-900">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaCheck size={18} />
            </div>

            <h3 className="mt-4 text-[18px] font-black">Order placed</h3>

            <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center dark:bg-slate-900">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300">
              <FaTrash size={18} />
            </div>

            <h3 className="mt-4 text-[18px] font-black">Order failed</h3>

            <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
